import {Component, ElementRef, inject, Input, OnInit, Renderer2, TemplateRef} from '@angular/core';
import {NgbDropdown, NgbOffcanvas} from '@ng-bootstrap/ng-bootstrap';
import { Menu, NavService } from '@/core/services/nav.service';
import { SwitcherComponent } from '@/shared/components/switcher/switcher.component';
import { AppStateService } from '@/core/services/app-state.service';
import {User} from "@/core/models/user.model";
import {interval, of, startWith} from "rxjs";
import {VoiceJob} from "@/core/models/job.model";
import {map} from "rxjs/operators";
import {Router, RouterLink} from "@angular/router";
import {SessionStorageService} from "@/core/services/internal/session-storage.service";
import {SsoService} from "@/core/services/external/sso.service";
import {Formatters} from "@/core/helpers/formatters";

import {MaterialModuleModule} from "@/material-module/material-module.module";
import {OverlayscrollbarsModule} from "overlayscrollbars-ngx";

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    MaterialModuleModule,
    RouterLink,
    NgbDropdown,
    OverlayscrollbarsModule
  ],
  standalone: true
})
export class HeaderComponent implements OnInit{
  @Input() profile!: User;

  public analyses: any[] = [];
  public selectedJobId: string | null = null;
  public showConversationSelector = false;

  public runningJob: any = null;
  public remainingTime$ = of('');

  public selectedAnalysis: any = null;

  constructor(
    public elementRef: ElementRef,
    public NavServices: NavService,
    private appStateService: AppStateService,
    private appState: AppStateService,
    private router: Router,
    private sessionStorage: SessionStorageService,
    private ssoService: SsoService,
    public formatters: Formatters
  ) {

  }

  isFullscreen: boolean = false;
  public menuItems!: Menu[];
  public items!: Menu[];
  public text!: string;

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
          .then(() => {
            this.isFullscreen = true;
          })
          .catch(err => {
            console.error('Error entering fullscreen:', err);
          });
    } else {
      document.exitFullscreen()
          .then(() => {
            this.isFullscreen = false;
          })
          .catch(err => {
            console.error('Error exiting fullscreen:', err);
          });
    }
  }

  ngOnInit(): void {
    this.NavServices.items.subscribe((menuItems) => {
      this.items = menuItems;
    });

    this.text = '';

    this.appState.analyses$.subscribe(a => {
      this.analyses = (a || []).filter(x =>
          (x.speakers || []).every((s: any) => s.validated)
      );

      const currentSelected = this.appState.getSelectedAnalysis();

      if (currentSelected) {
        const updatedSelected = this.analyses.find(
            x => x.jobId === currentSelected.jobId
        );

        if (updatedSelected) {
          this.selectedJobId = updatedSelected.jobId;

          // keep latest object reference synced
          this.appState.setSelectedAnalysis(updatedSelected);
        }
      }
    });

    this.appState.selectedAnalysis$.subscribe(a => {
      this.selectedAnalysis = a;
      this.selectedJobId = a?.jobId;

      if (!a) {
        this.showConversationSelector = this.analyses.length > 0;
        return;
      }

      const hasUnvalidatedSpeakers =
          (a.speakers || []).some((s: any) => !s.validated);

      this.showConversationSelector = !hasUnvalidatedSpeakers;
    });

    this.appState.runningJob$.subscribe(job => {
      this.runningJob = job;

      this.runTime(job);

    });
  }

  isAdmin(): boolean {
    return (
        this.profile?.first_name === 'Hafid' &&
        this.profile?.email === 'abdelhafid.dahhani@gmail.com'
    );
  }

  runTime(job: VoiceJob) {
    if (!job) {
      this.remainingTime$ = of('');
      return;
    }

    const createdAt = new Date(
        `${job.createdAt}Z`
    ).getTime();

    const estimatedDuration =
        Math.floor(job.durationSec || 0);

    this.remainingTime$ = interval(1000).pipe(
        startWith(0),
        map(() => {
          const elapsedSeconds = Math.floor(
              (Date.now() - createdAt) / 1000
          );

          const remaining =
              estimatedDuration - elapsedSeconds;

          if (remaining <= 0) {
            return 'Finished, reload page...';
          }

          const minutes = Math.floor(remaining / 60);
          const seconds = remaining % 60;

          return `${minutes}m ${seconds}s remaining`;
        })
    );
  }

  trackByJob(index: number, item: any) {
    return item.jobId;
  }

  onSelectAnalysis(jobId: string) {
    const selected = this.analyses.find(a => a.jobId === jobId);
    if (!selected) return;

    this.appState.selectAnalysis(jobId);
  }

  private offcanvasService = inject(NgbOffcanvas);
  toggleSwitcher() {
    this.offcanvasService.open(SwitcherComponent, {
      position: 'end',
      scroll: true,
    });
  }

  logout(): void {
    this.ssoService.logout().subscribe({
      next: () => {
        this.clearLocalSession();
      },
      error: (err: any) => {
        console.error('Logout failed:', err);

        // still logout locally
        this.clearLocalSession();
      }
    });
  }

  private clearLocalSession(): void {
    this.appState.setSelectedAnalysis(null);
    this.router.navigate(['/auth/login']);
  }

  updateTheme(theme: string) {
    this.appStateService.updateState({ theme , menuColor:theme });
    if(theme=='light'){
      this.appStateService.updateState({ theme,themeBackground : '', headerColor:'light', menuColor:'light' });
      let html = document.querySelector('html');
      html?.style.removeProperty( '--body-bg-rgb');
      html?.style.removeProperty( '--body-bg-rgb2');
      html?.style.removeProperty( '--light-rgb');
      html?.style.removeProperty( '--form-control-bg');
      html?.style.removeProperty( '--input-border' );
      // html?.style.removeProperty('--primary');
      html?.setAttribute('data-toggled','close')
      html?.style.removeProperty('--primary-rgb');
    }
    if(theme=='dark'){
      this.appStateService.updateState({ theme,themeBackground : '', headerColor:'dark', menuColor:'dark' });
      let html = document.querySelector('html');
      html?.style.removeProperty( '--body-bg-rgb');
      html?.style.removeProperty( '--body-bg-rgb2');
      html?.style.removeProperty( '--light-rgb');
      html?.style.removeProperty( '--form-control-bg');
      html?.style.removeProperty( '--input-border' );
      // html?.style.removeProperty('--primary');
      html?.setAttribute('data-toggled','close')
      html?.style.removeProperty('--primary-rgb');

    }
  }

  toggleSidebar() {
    let html = this.elementRef.nativeElement.ownerDocument.documentElement;
    if (html?.getAttribute('data-toggled') == 'true') {
      document.querySelector('html')?.getAttribute('data-toggled') == 'icon-overlay-close';
    }
    else if (html?.getAttribute('data-nav-style') == 'menu-click') {
      html?.setAttribute(
        'data-toggled',
        html?.getAttribute('data-toggled') == 'menu-click-closed'
          ? ''
          : 'menu-click-closed'
      );
    } else if (html?.getAttribute('data-nav-style') == 'menu-hover') {
      html?.setAttribute(
        'data-toggled',
        html?.getAttribute('data-toggled') == 'menu-hover-closed'
          ? ''
          : 'menu-hover-closed'
      );
    } else if (html?.getAttribute('data-nav-style') == 'icon-click') {
      html?.setAttribute(
        'data-toggled',
        html?.getAttribute('data-toggled') == 'icon-click-closed'
          ? ''
          : 'icon-click-closed'
      );
    } else if (html?.getAttribute('data-nav-style') == 'icon-hover') {
      html?.setAttribute(
        'data-toggled',
        html?.getAttribute('data-toggled') == 'icon-hover-closed'
          ? ''
          : 'icon-hover-closed'
      );
    }
    else if (html?.getAttribute('data-vertical-style') == 'overlay') {
      html?.setAttribute(
        'data-vertical-style','overlay'
      );
      html?.setAttribute(
        'data-toggled', html?.getAttribute('data-toggled') == 'icon-overlay-close'
          ? ''
          : 'icon-overlay-close'
      );
    } else if (html?.getAttribute('data-vertical-style')  == 'overlay') {
      document.querySelector('html')?.getAttribute('data-toggled') != null
        ? document.querySelector('html')?.removeAttribute('data-toggled')
        : document
          .querySelector('html')
          ?.setAttribute('data-toggled', 'icon-overlay-close');
    } else if (html?.getAttribute('data-vertical-style') == 'closed') {
      html?.setAttribute(
        'data-toggled',
        html?.getAttribute('data-toggled') == 'close-menu-close'
          ? ''
          : 'close-menu-close'
      );
    } else if (html?.getAttribute('data-vertical-style') == 'icontext') {
      html?.setAttribute(
        'data-toggled',
        html?.getAttribute('data-toggled') == 'icon-text-close'
          ? ''
          : 'icon-text-close'
      );
    } else if (html?.getAttribute('data-vertical-style') == 'detached') {
      html?.setAttribute(
        'data-toggled',
        html?.getAttribute('data-toggled') == 'detached-close'
          ? ''
          : 'detached-close'
      );
    }else if (html?.getAttribute('data-vertical-style') == 'doublemenu') {
      html?.setAttribute('data-toggled', html?.getAttribute('data-toggled') == 'double-menu-close' && document.querySelector(".slide.open")?.classList.contains("has-sub")? 'double-menu-open': 'double-menu-close' );
    }

    if (window.innerWidth <= 992) {
      html?.setAttribute(
        'data-toggled',
        html?.getAttribute('data-toggled') == 'open' ? 'close' : 'open'
      );
    }
  }
}
